import {Map} from "immutable";
import * as React from "react";
import {HTMLAttributes, useCallback, useEffect, useState} from "react";
import {ThemeProvider} from "@mui/material/styles";
import {createTheme, FilterOptionsState} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {connect, useDispatch} from "react-redux";
import {AnyAction, bindActionCreators, Dispatch} from "redux";
import {FormattedMessage} from "react-intl";
import {
    isEditingPlanPortfolio,
    portfolioProductSummariesSelector,
    productUniverseSummariesSelector,
} from "../../../mainReducerMapSelectors";
import {IApplicationRootState} from "../../../applicationState";
import {ProductSummary} from "../../model/product/ProductSummary";
import {ResearchSearchItem} from "../../model/Search.model";
import {getSearchItemsFromProducts} from "../../utils/researchUtil";
import {hasConsultantLevelAccess} from "../../utils/sessionUtil";
import {getAllProducts, getPortfolioProductSummaries} from "../product/AllProductsActions";
import {getSuccessData, REQUEST_STATES, RequestState} from "../common/commonStates";
import productActions, {default as ProductActions} from "../product/productActions";
import {AonColors} from "../../../../css/Colors";
import {getTranslation} from "../../utils/translationUtil";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";
import {AnyThunkDispatch} from "../../../commonTypes";

const NO_RESULTS_MESSAGE = "No strategy or manager results found.";

const searchTheme = createTheme({
    components: {
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: `${AonColors.AonBlack} !important`,
                    opacity: "0.4 !important",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderWidth: "0px 0px 2px 0px !important",
                    borderColor: `${AonColors.AonTealDark} !important`,
                    borderRadius: "0px !important",
                },
            },
        },
    },
});

export interface IResearchSearchPropsFromState {
    editingPlanPortfolio: boolean;
    productUniverseSummaries: Map<number, ProductSummary> | null;
    portfolioProductSummaries: Map<number, ProductSummary> | null;
    productUniverseSummariesState: RequestState<Map<number, ProductSummary>>;
    portfolioProductSummariesState: RequestState<Map<number, ProductSummary>>;
}

export interface IResearchSearchPropsFromActions {
    actions: {
        selectTicker: typeof ProductActions.selectTicker;
    };
}

export type ResearchSearchProps = IResearchSearchPropsFromState & IResearchSearchPropsFromActions;

export const ResearchSearchComponent: React.FunctionComponent<ResearchSearchProps> = (props) => {
    const dispatch: AnyThunkDispatch = useDispatch();
    const navigate = useNavigate();
    const [searchItems, setSearchItems] = useState<ResearchSearchItem[]>([]);
    const [searchText, setSearchText] = useState<string>("");

    const NO_RESULTS_MESSAGE_TRANSLATED = getTranslation("subnav.search-no-results", NO_RESULTS_MESSAGE);

    useEffect(() => {
        const isConsultantEditing = hasConsultantLevelAccess() && props.editingPlanPortfolio;
        if (isConsultantEditing) {
            const shouldRequestUniverse = props.productUniverseSummariesState.kind === REQUEST_STATES.NOT_REQUESTED
                || props.productUniverseSummariesState.kind === REQUEST_STATES.REQUEST_FAILED;
            if (!props.productUniverseSummaries && shouldRequestUniverse) {
                dispatch(getAllProducts);
            }
            return;
        }

        const shouldRequestPortfolio = props.portfolioProductSummariesState.kind === REQUEST_STATES.NOT_REQUESTED
            || props.portfolioProductSummariesState.kind === REQUEST_STATES.REQUEST_FAILED;
        if (!props.portfolioProductSummaries && shouldRequestPortfolio) {
            dispatch(getPortfolioProductSummaries);
        }
    }, [
        dispatch,
        props.editingPlanPortfolio,
        props.portfolioProductSummaries,
        props.portfolioProductSummariesState.kind,
        props.productUniverseSummaries,
        props.productUniverseSummariesState.kind,
    ]);

    const getSearchItems = useCallback((): ResearchSearchItem[] => {
        const universeSummaries = props.productUniverseSummaries?.valueSeq().toArray();
        const portfolioSummaries = props.portfolioProductSummaries?.valueSeq().toArray();
        const productSummaries = (hasConsultantLevelAccess() && props.editingPlanPortfolio)
            ? (universeSummaries && universeSummaries.length > 0 ? universeSummaries : portfolioSummaries)
            : portfolioSummaries;
        return productSummaries ? getSearchItemsFromProducts(productSummaries) : [];
    }, [props.editingPlanPortfolio, props.portfolioProductSummaries, props.productUniverseSummaries]);

    const setItemsToSearch = useCallback(() => {
        const items = getSearchItems().slice(0).sort((a, b) =>
            a.displayName.trim().toLowerCase().localeCompare(b.displayName.trim().toLowerCase())
        );
        items.push(new ResearchSearchItem(-1, NO_RESULTS_MESSAGE_TRANSLATED, "NOT_FOUND", "", undefined));
        setSearchItems(items);
    }, [getSearchItems, NO_RESULTS_MESSAGE_TRANSLATED]);

    useEffect(() => {
        setItemsToSearch();
    }, [setItemsToSearch]);

    function handleSearchItemSelected(item: ResearchSearchItem | string) {
        if (typeof item === "string") return;
        if (item.itemType !== "NOT_FOUND") {
            if (item.tickerId) {
                props.actions.selectTicker({tickerId: item.tickerId, productId: item.backstopId});
            }
            setSearchText("");
            navigateTo(item.pageUrl, navigate);
        } else {
            setSearchText("");
        }
    }

    function searchFilter(
        options: ResearchSearchItem[],
        state: FilterOptionsState<ResearchSearchItem>
    ): ResearchSearchItem[] {
        if (state.inputValue.length < 2) {
            return [];
        }
        const newOptions: ResearchSearchItem[] = [];
        options.forEach((element) => {
            if (
                element.displayName.toLowerCase().includes(state.inputValue.toLowerCase())
                && element.displayName !== NO_RESULTS_MESSAGE_TRANSLATED
            ) {
                newOptions.push(element);
            }
        });
        if (newOptions.length === 0) {
            newOptions.push(new ResearchSearchItem(-1, NO_RESULTS_MESSAGE_TRANSLATED, "NOT_FOUND", "", undefined));
        }
        return newOptions;
    }

    return (
        <div className="research-search" data-testid="research-search">
            <span id="research-search-label" className="research-search__label">
                <FormattedMessage id="research-search.add-a-strategy" defaultMessage="Add a strategy:" />
            </span>
            <div className="research-search__input-container" data-testid="research-search__input-container">
                <i className="research-search__icon fa-regular fa-magnifying-glass" aria-hidden="true" />
                <div className="research-search__autocomplete" id="research-search-autocomplete-container">
                    <ThemeProvider theme={searchTheme}>
                        <Autocomplete
                            options={searchItems}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={getTranslation(
                                        "subnav.search-managers-strategies",
                                        "Search managers or strategies"
                                    )}
                                    slotProps={{
                                        ...params.slotProps,
                                        htmlInput: {
                                            ...(params.slotProps?.htmlInput as object),
                                            "aria-describedby": "research-search-label",
                                        },
                                    }}
                                />
                            )}
                            className="research-search__field"
                            getOptionLabel={(option: ResearchSearchItem) => option.displayName}
                            onChange={(event, value: ResearchSearchItem | string | null) => {
                                if (value) handleSearchItemSelected(value);
                            }}
                            onInputChange={(event, newValue) => {
                                setSearchText(newValue);
                            }}
                            inputValue={searchText}
                            fullWidth={true}
                            size="small"
                            filterOptions={searchFilter}
                            freeSolo={true}
                            selectOnFocus={true}
                            getOptionDisabled={(option: ResearchSearchItem) => option.backstopId === -1}
                            renderOption={(liProps: HTMLAttributes<HTMLLIElement>, option: ResearchSearchItem) => (
                                <li {...liProps} key={`${option.backstopId}${option.displayName}`}>
                                    {option.displayName}
                                </li>
                            )}
                            data-testid="research-search__field"
                        />
                    </ThemeProvider>
                </div>
            </div>
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState): IResearchSearchPropsFromState => ({
    editingPlanPortfolio: isEditingPlanPortfolio(state),
    productUniverseSummaries: getSuccessData(productUniverseSummariesSelector(state))!,
    portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state))!,
    productUniverseSummariesState: productUniverseSummariesSelector(state)!,
    portfolioProductSummariesState: portfolioProductSummariesSelector(state)!,
});

export const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IResearchSearchPropsFromActions => ({
    actions: bindActionCreators({
        selectTicker: productActions.selectTicker,
    }, dispatch),
});

const ResearchSearch = connect<IResearchSearchPropsFromState, IResearchSearchPropsFromActions>(
    mapStateToProps, mapDispatchToProps
)(ResearchSearchComponent);

export default ResearchSearch;
